import React from 'react';

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';

import { PageProps } from '../../../components/types/coretypes';

import BieOnboardingStatusChip from './BieOnboardingStatusChip';
import BieOnboardingStagingReview from './BieOnboardingStagingReview';

import {
  addBieLoadSession,
  editBieLoadSession,
  getBieLoadSession,
  reopenBieLoadSession,
} from './bieloadsession.apis';

import {
  BieLoadSession,
  BieLoadSessionAddPayload,
  BieLoadSessionMode,
} from './bieloadsession.models';

import {
  reviewBieOnboardingWorkbook,
  uploadBieOnboardingWorkbook,
} from './bieonboardingworkbook.apis';

import {
  BieOnboardingWorkbookReview,
} from './bieonboardingworkbook.models';

import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';

/* ============================================================
 * Page constants
 * ============================================================
 */

const PAGE_LOAD_SESSION_MANAGER = 'bieonboardingsession';
const PAGE_LOAD_SESSION_WIZARD  = 'bieonboardingwizard';
const PAGE_ONBOARDING_WORKBENCH = 'bieonboardingreview';

/* ============================================================
 * Page-only models
 *
 * Domain and API contracts remain in:
 *   - bieloadsession.models.ts
 *   - bieonboardingworkbook.models.ts
 * ============================================================
 */

export type BieOnboardingWizardStepCode =
  | 'LOAD_CONTEXT'
  | 'WORKBOOK_UPLOAD'
  | 'STAGING_REVIEW';

export interface IBieOnboardingLookupOption {
  rid: number | '';
  code: string;
  name: string;
  disabled?: boolean;
}

type BieOnboardingLoadContext = Omit<
  BieLoadSessionAddPayload,
  'extraInfo'
> & {
  institutionId: number | '';
  institutionCode: string;
  institutionName: string;
  extraInfo: Record<string, any>;
};

export interface IBieOnboardingLoadSessionWizardProps extends PageProps {
  institutionOptions?: IBieOnboardingLookupOption[];
  loadScopeOptions?: IBieOnboardingLookupOption[];

  /**
   * Template delivery may remain framework-specific. Session CRUD,
   * workbook upload/review and routing are not injected through props.
   */
  downloadTemplate?: () => Promise<void> | void;
}

interface IBieOnboardingLoadSessionWizardState {
  mode: BieLoadSessionMode;
  activeStep: number;

  loading: boolean;
  saving: boolean;
  uploading: boolean;
  loadingReview: boolean;

  errorMessage: string;
  successMessage: string;

  loadSessionId: number | '';
  stamp: string;
  stateCode: string;

  context: BieOnboardingLoadContext;
  contextErrors: Partial<
    Record<keyof BieOnboardingLoadContext, string>
  >;

  selectedFile: File | null;
  stagingReview: BieOnboardingWorkbookReview | null;

  reopening: boolean;
}

/* ============================================================
 * Defaults
 * ============================================================
 */

const wizardSteps: Array<{
  code: BieOnboardingWizardStepCode;
  label: string;
  description: string;
}> = [
  {
    code: 'LOAD_CONTEXT',
    label: 'Load Context',
    description: 'Define the institution, actual period and forecast horizon.',
  },
  {
    code: 'WORKBOOK_UPLOAD',
    label: 'Workbook Upload',
    description: 'Upload the completed institutional onboarding workbook.',
  },
  {
    code: 'STAGING_REVIEW',
    label: 'Staging Review',
    description:
      'Review extracted worksheets and open the validation Workbench.',
  },
];

const emptyLoadContext: BieOnboardingLoadContext = {
  name: '',
  shortcode: '',
  description: '',

  scopeId: 0,
  sourceOrganization: '',
  sourceSystem: '',
  coverageStartDate: '',
  coverageEndDate: '',
  historicalCutoffDate: '',

  institutionId: '',
  institutionCode: '',
  institutionName: '',

  extraInfo: {},
};

/* ============================================================
 * Component
 * ============================================================
 */

/**
 * Guided Institutional Onboarding entry process:
 *
 * Load Context
 *      ↓
 * Direct add/edit API call
 *      ↓
 * Preserve returned rid and stp
 *      ↓
 * Workbook Upload against rid
 *      ↓
 * Staging Review
 *      ↓
 * Workbench using the same rid
 */
export default class BieOnboardingLoadSessionWizard extends React.Component<
  IBieOnboardingLoadSessionWizardProps,
  IBieOnboardingLoadSessionWizardState
> {
  private readonly fileInputRef = React.createRef<HTMLInputElement>();

  private mounted = false;

  private requestVersion = 0;

  public constructor(props: IBieOnboardingLoadSessionWizardProps) {
    super(props);

    this.state = {
      mode: 'ADD',
      activeStep: 0,

      loading: false,
      saving: false,
      uploading: false,
      loadingReview: false,

      errorMessage: '',
      successMessage: '',

      loadSessionId: '',
      stamp: '',
      stateCode: 'DRAFT',

      context: {
        ...emptyLoadContext,
      },

      contextErrors: {},

      selectedFile: null,
      stagingReview: null,

      reopening: false,
    };
  }

  public componentDidMount(): void {
    this.mounted = true;
    this.initializeFromPageContext();
  }

  public componentDidUpdate(
    previousProps: IBieOnboardingLoadSessionWizardProps
  ): void {
    const previousId = Number(
      previousProps.params?.loadsessionid || 0
    );

    const currentId = Number(
      this.props.params?.loadsessionid || 0
    );

    const previousMode = String(
      previousProps.params?.mode || 'ADD'
    ).toUpperCase();

    const currentMode = String(
      this.props.params?.mode || 'ADD'
    ).toUpperCase();

    const previousStep = String(
      previousProps.params?.startstep || 'LOAD_CONTEXT'
    ).toUpperCase();

    const currentStep = String(
      this.props.params?.startstep || 'LOAD_CONTEXT'
    ).toUpperCase();

    if (
      previousId !== currentId ||
      previousMode !== currentMode ||
      previousStep !== currentStep
    ) {
      this.initializeFromPageContext();
    }
  }

  public componentWillUnmount(): void {
    this.mounted = false;
    this.requestVersion += 1;
  }

  public render(): React.ReactNode { 
    return (
      <Stack spacing={2}>
        {this.renderHeader()}
        {this.renderStepper()}

        {this.state.errorMessage && (
          <Alert severity="error" onClose={this.handleClearError}>
            {this.state.errorMessage}
          </Alert>
        )}

        {this.state.successMessage && (
          <Alert severity="success" onClose={this.handleClearSuccess}>
            {this.state.successMessage}
          </Alert>
        )}

        {this.state.loading ? this.renderLoading() : this.renderActiveStep()}
      </Stack>
    );
  }

  /* ==========================================================
   * Initialization
   * ==========================================================
   */

  private initializeFromPageContext(): void {
    const requestedMode = String(
      this.props.params?.mode || 'ADD'
    ).toUpperCase();

    const loadSessionId = Number(
      this.props.params?.loadsessionid || 0
    );

    const startStep = this.normalizeStepCode(
      this.props.params?.startstep
    );

    if (loadSessionId > 0) {
      const mode: BieLoadSessionMode =
        requestedMode === 'VIEW' ? 'VIEW' : 'EDIT';

      this.setState(
        {
          mode,
          loadSessionId,
          loading: true,
          errorMessage: '',
          successMessage: '',
        },
        () => {
          void this.loadExistingSession(loadSessionId, startStep);
        }
      );

      return;
    }

    this.requestVersion += 1;

    this.setState({
      mode: 'ADD',
      activeStep: 0,

      loading: false,
      saving: false,
      uploading: false,
      loadingReview: false,

      errorMessage: '',
      successMessage: '',

      loadSessionId: '',
      stamp: '',
      stateCode: 'DRAFT',

      context: {
        ...emptyLoadContext,
      },

      contextErrors: {},
      selectedFile: null,
      stagingReview: null,
    });
  }

  private async loadExistingSession(
    loadSessionId: number,
    requestedStep: BieOnboardingWizardStepCode
  ): Promise<void> {
    const requestVersion = ++this.requestVersion;

    this.setState({
      loading: true,
      errorMessage: '',
    });

    try {
      const session = await getBieLoadSession(loadSessionId);

      if (!this.mounted || requestVersion !== this.requestVersion) {
        return;
      }

      let stagingReview: BieOnboardingWorkbookReview | null = null;

      if (session.workbook.available) {
        stagingReview = await reviewBieOnboardingWorkbook(loadSessionId);
      }

      if (!this.mounted || requestVersion !== this.requestVersion) {
        return;
      }

      this.setState({
        loading: false,

        loadSessionId: session.rid,
        stamp: session.stamp,
        stateCode: session.stateCode,

        context: this.extractContext(session),
        contextErrors: {},

        stagingReview,
        activeStep: this.resolveInitialStep(
          requestedStep,
          session,
          stagingReview
        ),
      });
    } catch (error) {
      if (!this.mounted || requestVersion !== this.requestVersion) {
        return;
      }

      this.setState({
        loading: false,
        errorMessage: this.getErrorMessage(error),
      });
    }
  }

  /* ==========================================================
   * Header
   * ==========================================================
   */

  private renderHeader(): React.ReactNode {
    const editing = Boolean(this.state.loadSessionId);

    return (
      <Paper
        variant="outlined"
        sx={{
          p: {
            xs: 1.5,
            sm: 2,
          },
        }}
      >
        <Stack
          direction={{
            xs: 'column',
            sm: 'row',
          }}
          justifyContent="space-between"
          alignItems={{
            xs: 'stretch',
            sm: 'center',
          }}
          spacing={2}
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Tooltip title="Return to Load Sessions" arrow>
              <IconButton
                disabled={this.isBusy()}
                onClick={this.handleBackToManager}
              >
                <ArrowBackOutlinedIcon />
              </IconButton>
            </Tooltip>

            <Box
              sx={{
                width: 44,
                height: 44,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 2,
                color: 'primary.main',
                backgroundColor: 'action.hover',
              }}
            >
              <BusinessOutlinedIcon />
            </Box>

            <Box>
              <Typography variant="h5" fontWeight={700}>
                {editing
                  ? 'Institutional Onboarding Session'
                  : 'New Institutional Onboarding'}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                {editing
                  ? `${this.state.context.shortcode || 'Draft Session'} · ` +
                    `Load Session ${String(this.state.loadSessionId)}`
                  : 'Create the Load Session before uploading institutional data.'}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {editing && (
              <>
                <BieOnboardingStatusChip
                  kind="LOAD_SESSION"
                  value={this.state.stateCode}
                />

                <Chip
                  size="small"
                  variant="outlined"
                  label={`ID ${String(this.state.loadSessionId)}`}
                />
              </>
            )}
          </Stack>
        </Stack>
      </Paper>
    );
  }

  /* ==========================================================
   * Stepper
   * ==========================================================
   */

  private renderStepper(): React.ReactNode {
    return (
      <Paper
        variant="outlined"
        sx={{
          p: {
            xs: 1.5,
            md: 2,
          },
        }}
      >
        <Stepper activeStep={this.state.activeStep} alternativeLabel>
          {wizardSteps.map((step, index) => (
            <Step key={step.code} completed={this.isStepCompleted(index)}>
              <StepLabel
                optional={
                  <Typography variant="caption" color="text.secondary">
                    {step.description}
                  </Typography>
                }
              >
                {step.label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>
    );
  }

  /* ==========================================================
   * Active step
   * ==========================================================
   */

  private renderActiveStep(): React.ReactNode {
    switch (this.state.activeStep) {
      case 1:
        return this.renderWorkbookUploadStep();

      case 2:
        return this.renderStagingReviewStep();

      case 0:
      default:
        return this.renderLoadContextStep();
    }
  }

  /* ==========================================================
   * Step 1: Load Context
   * ==========================================================
   */

  private renderLoadContextStep(): React.ReactNode {
    const contextReadOnly = !this.canEditContext();

    return (
      <Paper
        variant="outlined"
        sx={{
          p: {
            xs: 2,
            md: 2.5,
          },
        }}
      >
        <Stack spacing={2.5}>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <CalendarMonthOutlinedIcon color="primary" />

              <Typography variant="h6" fontWeight={700}>
                Load Context
              </Typography>
            </Stack>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5 }}
            >
              Define the institution and the period boundaries for this
              onboarding exercise.
            </Typography>
          </Box>

          {contextReadOnly && (
            <Alert severity="info">
              The Load Context is read-only because the session has progressed
              beyond Draft or the page was opened in View mode.
            </Alert>
          )}

          <Divider />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(2, minmax(0, 1fr))',
              },
              gap: 2,
            }}
          >
            <TextField
              required
              fullWidth
              size="small"
              label="Session Code"
              value={this.state.context.shortcode}
              disabled={contextReadOnly}
              error={Boolean(this.state.contextErrors.shortcode)}
              helperText={
                this.state.contextErrors.shortcode ||
                'Unique code for the onboarding exercise.'
              }
              inputProps={{ maxLength: 50 }}
              onChange={this.handleShortcodeChange}
            />

            <TextField
              required
              fullWidth
              size="small"
              label="Session Name"
              value={this.state.context.name}
              disabled={contextReadOnly}
              error={Boolean(this.state.contextErrors.name)}
              helperText={this.state.contextErrors.name}
              inputProps={{ maxLength: 200 }}
              onChange={this.handleNameChange}
            />

            {this.renderInstitutionField(contextReadOnly)}

            <TextField
              required
              fullWidth
              size="small"
              label="Source Organization"
              value={this.state.context.sourceOrganization}
              disabled={contextReadOnly}
              error={Boolean(this.state.contextErrors.sourceOrganization)}
              helperText={this.state.contextErrors.sourceOrganization}
              inputProps={{ maxLength: 200 }}
              onChange={this.handleSourceOrganizationChange}
            />

            {this.renderLoadScopeField(contextReadOnly)}

            <TextField
              required
              fullWidth
              size="small"
              label="Source System"
              value={this.state.context.sourceSystem}
              disabled={contextReadOnly}
              error={Boolean(this.state.contextErrors.sourceSystem)}
              helperText={this.state.contextErrors.sourceSystem}
              inputProps={{ maxLength: 100 }}
              onChange={this.handleSourceSystemChange}
            />

            <TextField
              required
              fullWidth
              size="small"
              type="date"
              label="Coverage Start Date"
              value={this.state.context.coverageStartDate}
              disabled={contextReadOnly}
              error={Boolean(this.state.contextErrors.coverageStartDate)}
              helperText={this.state.contextErrors.coverageStartDate}
              InputLabelProps={{ shrink: true }}
              onChange={this.handleCoverageStartDateChange}
            />

            <TextField
              required
              fullWidth
              size="small"
              type="date"
              label="Coverage End Date"
              value={this.state.context.coverageEndDate}
              disabled={contextReadOnly}
              error={Boolean(this.state.contextErrors.coverageEndDate)}
              helperText={this.state.contextErrors.coverageEndDate}
              InputLabelProps={{ shrink: true }}
              onChange={this.handleCoverageEndDateChange}
            />

            <TextField
              required
              fullWidth
              size="small"
              type="date"
              label="Historical Cut-off Date"
              value={this.state.context.historicalCutoffDate}
              disabled={contextReadOnly}
              error={Boolean(this.state.contextErrors.historicalCutoffDate)}
              helperText={this.state.contextErrors.historicalCutoffDate}
              InputLabelProps={{ shrink: true }}
              onChange={this.handleHistoricalCutoffDateChange}
            />
          </Box>

          <TextField
            fullWidth
            multiline
            minRows={3}
            maxRows={6}
            label="Description"
            value={this.state.context.description || ''}
            disabled={contextReadOnly}
            inputProps={{ maxLength: 2000 }}
            onChange={this.handleDescriptionChange}
          />

          <Divider />

          <Stack
            direction={{
              xs: 'column',
              sm: 'row',
            }}
            justifyContent="space-between"
            spacing={1}
          >
            <Button
              color="inherit"
              disabled={this.isBusy()}
              onClick={this.handleBackToManager}
            >
              Cancel
            </Button>

            <Stack direction="row" spacing={1}>
              {contextReadOnly && this.state.loadSessionId && (
                <Button
                  variant="outlined"
                  endIcon={<ArrowForwardOutlinedIcon />}
                  onClick={() => this.setState({ activeStep: 1 })}
                >
                  Continue
                </Button>
              )}

              {!contextReadOnly && (
                <Button
                  variant="contained"
                  disabled={this.isBusy()}
                  startIcon={
                    this.state.saving ? (
                      <CircularProgress size={18} />
                    ) : (
                      <SaveOutlinedIcon />
                    )
                  }
                  endIcon={
                    !this.state.saving ? (
                      <ArrowForwardOutlinedIcon />
                    ) : undefined
                  }
                  onClick={this.handleSaveContext}
                >
                  {this.state.saving
                    ? 'Saving…'
                    : this.state.loadSessionId
                      ? 'Save and Continue'
                      : 'Create Session'}
                </Button>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Paper>
    );
  }

  private renderInstitutionField(disabled: boolean): React.ReactNode {
    const options = this.props.institutionOptions || [];

    if (options.length === 0) {
      return (
        <TextField
          required
          fullWidth
          size="small"
          label="Institution"
          value={this.state.context.institutionName}
          disabled={disabled}
          error={Boolean(this.state.contextErrors.institutionName)}
          helperText={this.state.contextErrors.institutionName}
          onChange={this.handleInstitutionNameChange}
        />
      );
    }

    return (
      <TextField
        select
        required
        fullWidth
        size="small"
        label="Institution"
        value={String(this.state.context.institutionId || '')}
        disabled={disabled}
        error={Boolean(this.state.contextErrors.institutionId)}
        helperText={this.state.contextErrors.institutionId}
        onChange={this.handleInstitutionChange}
      >
        <MenuItem value="">Select Institution</MenuItem>

        {options.map((option) => (
          <MenuItem
            key={String(option.rid || option.code)}
            value={String(option.rid)}
            disabled={option.disabled}
          >
            {option.name}
            {option.code ? ` (${option.code})` : ''}
          </MenuItem>
        ))}
      </TextField>
    );
  }

  private renderLoadScopeField(disabled: boolean): React.ReactNode {
    const options = this.props.params?.loadScopeOptions || [];

    if (options.length === 0) {
      return (
        <TextField
          required
          fullWidth
          size="small"
          label="Load Scope"
          value={String(this.state.context.scopeId || '')}
          disabled={disabled}
          error={Boolean(this.state.contextErrors.scopeId)}
          helperText={this.state.contextErrors.scopeId}
          onChange={this.handleLoadScopeChange}
        />
      );
    }

    return (
      <TextField
        select
        required
        fullWidth
        size="small"
        label="Load Scope"
        value={String(this.state.context.scopeId || '')}
        disabled={disabled}
        error={Boolean(this.state.contextErrors.scopeId)}
        helperText={this.state.contextErrors.scopeId}
        onChange={this.handleLoadScopeChange}
      >
        <MenuItem value="">Select Load Scope</MenuItem>

        {options.map((option:any) => (
          <MenuItem
            key={option.shc}
            value={String(option.rid)}
            disabled={option.disabled}
          >
            {option.nam}
          </MenuItem>
        ))}
      </TextField>
    );
  }

  /* ==========================================================
   * Step 2: Workbook Upload
   * ==========================================================
   */

  private renderWorkbookUploadStep(): React.ReactNode {
    const canUpload = this.canUploadWorkbook();

    return (
      <Paper
        variant="outlined"
        sx={{
          p: {
            xs: 2,
            md: 2.5,
          },
        }}
      >
        <Stack spacing={2.5}>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <CloudUploadOutlinedIcon color="primary" />

              <Typography variant="h6" fontWeight={700}>
                Workbook Upload
              </Typography>
            </Stack>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5 }}
            >
              Upload the completed BIE Institutional Onboarding workbook
              against Load Session{' '}
              <strong>{String(this.state.loadSessionId || '—')}</strong>.
            </Typography>
          </Box>

          {!this.state.loadSessionId && (
            <Alert severity="error">
              A Load Session must be created before a workbook can be uploaded.
            </Alert>
          )}

          {String(this.state.stateCode || '').toUpperCase() === 'REJECTED' && (
            <Alert
              severity="warning"
              action={
                <Button
                  color="inherit"
                  size="small"
                  disabled={this.state.reopening}
                  startIcon={
                    this.state.reopening ? (
                      <CircularProgress size={16} />
                    ) : (
                      <ReplayOutlinedIcon />
                    )
                  }
                  onClick={this.handleReopenForCorrection}
                >
                  {this.state.reopening
                    ? 'Reopening…'
                    : 'Reopen for Correction'}
                </Button>
              }
            >
              Validation found blocking issues. Reopen this Load Session before
              uploading a corrected workbook.
            </Alert>
          )}

          {!canUpload && this.state.loadSessionId && (
            <Alert severity="info">
              Workbook upload or replacement is not available while the Load
              Session is in the {this.state.stateCode} state.
            </Alert>
          )}

          <Divider />

          <input
            ref={this.fileInputRef}
            type="file"
            accept=".xlsx"
            hidden
            onChange={this.handleFileSelection}
          />

          <Box
            sx={{
              p: {
                xs: 2.5,
                md: 4,
              },
              border: '2px dashed',
              borderColor: this.state.selectedFile
                ? 'primary.main'
                : 'divider',
              borderRadius: 2,
              textAlign: 'center',
              backgroundColor: this.state.selectedFile
                ? 'primary.main' + '08'
                : 'action.hover',
            }}
          >
            <CloudUploadOutlinedIcon
              color={this.state.selectedFile ? 'primary' : 'disabled'}
              sx={{ fontSize: 54 }}
            />

            <Typography variant="h6" fontWeight={700} sx={{ mt: 1 }}>
              {this.state.selectedFile
                ? this.state.selectedFile.name
                : 'Select the onboarding workbook'}
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5 }}
            >
              Microsoft Excel workbook using the approved onboarding template.
            </Typography>

            {this.state.selectedFile && (
              <Stack
                direction="row"
                spacing={1}
                justifyContent="center"
                alignItems="center"
                sx={{ mt: 1.5 }}
              >
                <Chip
                  icon={<DescriptionOutlinedIcon />}
                  label={this.formatFileSize(this.state.selectedFile.size)}
                />

                <Tooltip title="Remove selected file" arrow>
                  <IconButton
                    color="error"
                    disabled={this.state.uploading}
                    onClick={this.handleClearSelectedFile}
                  >
                    <DeleteOutlineOutlinedIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            )}

            <Stack
              direction={{
                xs: 'column',
                sm: 'row',
              }}
              spacing={1}
              justifyContent="center"
              sx={{ mt: 2 }}
            >
              <Button
                variant="outlined"
                disabled={!canUpload || this.state.uploading}
                startIcon={<DescriptionOutlinedIcon />}
                onClick={this.handleBrowseFile}
              >
                {this.state.selectedFile
                  ? 'Choose Another File'
                  : 'Browse Workbook'}
              </Button>

              {this.props.downloadTemplate && (
                <Button
                  variant="text"
                  disabled={this.state.uploading}
                  startIcon={<DownloadOutlinedIcon />}
                  onClick={this.handleDownloadTemplate}
                >
                  Download Template
                </Button>
              )}
            </Stack>
          </Box>

          {this.state.uploading && (
            <Box>
              <LinearProgress />

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  mt: 0.75,
                  textAlign: 'center',
                }}
              >
                Parsing workbook and staging onboarding records…
              </Typography>
            </Box>
          )}

          <Alert severity="info">
            Uploading performs workbook parsing and staging only. Authoritative
            validation is performed from the Onboarding Workbench.
          </Alert>

          <Divider />

          <Stack
            direction={{
              xs: 'column',
              sm: 'row',
            }}
            justifyContent="space-between"
            spacing={1}
          >
            <Button
              color="inherit"
              disabled={this.isBusy()}
              startIcon={<ArrowBackOutlinedIcon />}
              onClick={() => this.setState({ activeStep: 0 })}
            >
              Load Context
            </Button>

            <Stack direction="row" spacing={1}>
              {this.state.stagingReview && (
                <Button
                  variant="outlined"
                  disabled={this.isBusy()}
                  endIcon={<ArrowForwardOutlinedIcon />}
                  onClick={() => this.setState({ activeStep: 2 })}
                >
                  Staging Review
                </Button>
              )}

              <Button
                variant="contained"
                disabled={
                  this.isBusy() || !canUpload || !this.state.selectedFile
                }
                startIcon={
                  this.state.uploading ? (
                    <CircularProgress size={18} />
                  ) : (
                    <CloudUploadOutlinedIcon />
                  )
                }
                onClick={this.handleUploadWorkbook}
              >
                {this.state.uploading
                  ? 'Uploading…'
                  : this.state.stagingReview
                    ? 'Replace Workbook'
                    : 'Upload and Stage'}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Paper>
    );
  }

  /* ==========================================================
   * Step 3: Staging Review
   * ==========================================================
   */

  private renderStagingReviewStep(): React.ReactNode {
    return (
      <BieOnboardingStagingReview
        review={this.state.stagingReview as any}
        loading={this.state.loadingReview}
        refreshing={
          this.state.loadingReview && Boolean(this.state.stagingReview)
        }
        disabled={this.state.uploading || this.state.saving}
        onBackToUpload={() => this.setState({ activeStep: 1 })}
        onRefresh={this.handleRefreshReview}
        onOpenWorkbench={this.handleOpenWorkbench}
      />
    );
  }

  private renderLoading(): React.ReactNode {
    return (
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack spacing={2}>
          <LinearProgress />
          <Skeleton variant="rounded" height={52} />
          <Skeleton variant="rounded" height={52} />
          <Skeleton variant="rounded" height={52} />
          <Skeleton variant="rounded" height={140} />
        </Stack>
      </Paper>
    );
  }

  /* ==========================================================
   * Save Load Context
   * ==========================================================
   */

  private readonly handleSaveContext = async (): Promise<void> => {
    const contextErrors = this.validateContext();

    if (Object.keys(contextErrors).length > 0) {
      this.setState({
        contextErrors,
        errorMessage:
          'Complete the required Load Context fields before continuing.',
      });

      return;
    }

    const creating = !this.state.loadSessionId;

    this.setState({
      saving: true,
      errorMessage: '',
      successMessage: '',
    });

    try {
      const payload = this.buildSavePayload();

      const result = creating
        ? await addBieLoadSession(payload)
        : await editBieLoadSession({
            ...payload,
            loadSessionId: Number(this.state.loadSessionId),
            stamp: this.state.stamp,
          });

      const session = await getBieLoadSession(result.rid);

      if (!this.mounted) {
        return;
      }

      const setWizardParam = this.props.onParam('set');

      setWizardParam('mode', 'EDIT');
      setWizardParam('loadsessionid', result.rid);
      setWizardParam('stp', result.stp);
      setWizardParam('startstep', 'WORKBOOK_UPLOAD');

      this.setState({
        saving: false,
        mode: 'EDIT',
        loadSessionId: result.rid,
        stamp: result.stp,
        stateCode: session.stateCode,
        context: this.extractContext(session),
        contextErrors: {},
        activeStep: 1,
        successMessage: creating
          ? 'Load Session created successfully.'
          : 'Load Context updated successfully.',
      });
    } catch (error) {
      if (!this.mounted) {
        return;
      }

      this.setState({
        saving: false,
        errorMessage: this.getErrorMessage(error),
      });
    }
  };

  /* ==========================================================
   * Workbook upload and review
   * ==========================================================
   */

  private readonly handleBrowseFile = (): void => {
    this.fileInputRef.current?.click();
  };

  private readonly handleFileSelection = (
    event: any //React.ChangeEvent<HTMLInputElement>
  ): void => {
    //const file = event.target.files?.[0] || null; 
    const files: File[] = event.target.files || event.dataTransfer.files;
    if (!files ) {
      return;
    }
      
    const file = files[0];

    if (!file) {
      return;
    }

    const extension = file.name.split('.').pop()?.toLowerCase();

    if (extension !== 'xlsx') {
      this.setState({
        selectedFile: null,
        errorMessage: 'Select a Microsoft Excel .xlsx workbook.',
      });

      event.target.value = '';
      return;
    }

    this.setState({
      selectedFile: file,
      errorMessage: '',
      successMessage: '',
    });
  };

  private readonly handleClearSelectedFile = (): void => {
    if (this.fileInputRef.current) {
      this.fileInputRef.current.value = '';
    }

    this.setState({
      selectedFile: null,
    });
  };

  private readonly handleUploadWorkbook = async (): Promise<void> => {
    const loadSessionId = Number(this.state.loadSessionId || 0);
    const selectedFile = this.state.selectedFile;

    if (loadSessionId <= 0) {
      this.setState({
        errorMessage: 'Create the Load Session before uploading a workbook.',
      });

      return;
    }

    if (!selectedFile) {
      this.setState({
        errorMessage: 'Select an onboarding workbook to upload.',
      });

      return;
    }

    this.setState({
      uploading: true,
      errorMessage: '',
      successMessage: '',
    });

    try {
      await uploadBieOnboardingWorkbook({
        loadSessionId,
        file: selectedFile,
        //replaceExisting: Boolean(this.state.stagingReview), 
        replaceExisting: false, 
      });

      const review = await reviewBieOnboardingWorkbook(loadSessionId);

      if (!this.mounted) {
        return;
      }

      this.setState({
        uploading: false,
        loadSessionId,
        stamp: review.stamp || this.state.stamp,
        stateCode: review.stateCode || this.state.stateCode,
        selectedFile: null,
        stagingReview: review,
        activeStep: 2,
        successMessage: 'Workbook parsed and staged successfully.',
      });

      const setWizardParam = this.props.onParam('set');

      setWizardParam('mode', 'EDIT');
      setWizardParam('loadsessionid', loadSessionId);
      setWizardParam('stp', review.stamp || this.state.stamp);
      setWizardParam('startstep', 'STAGING_REVIEW');

      if (this.fileInputRef.current) {
        this.fileInputRef.current.value = '';
      }
    } catch (error) {
      if (!this.mounted) {
        return;
      }

      this.setState({
        uploading: false,
        errorMessage: this.getErrorMessage(error),
      });
    }
  };

  private async loadStagingReview(): Promise<void> {
    const loadSessionId = Number(this.state.loadSessionId || 0);

    if (loadSessionId <= 0) {
      return;
    }

    const requestVersion = ++this.requestVersion;

    this.setState({
      loadingReview: true,
      errorMessage: '',
    });

    try {
      const review = await reviewBieOnboardingWorkbook(loadSessionId);

      if (!this.mounted || requestVersion !== this.requestVersion) {
        return;
      }

      this.setState({
        loadingReview: false,
        stagingReview: review,
        stamp: review.stamp || this.state.stamp,
        stateCode: review.stateCode || this.state.stateCode,
      });
    } catch (error) {
      if (!this.mounted || requestVersion !== this.requestVersion) {
        return;
      }

      this.setState({
        loadingReview: false,
        errorMessage: this.getErrorMessage(error),
      });
    }
  }

  private readonly handleRefreshReview = (): void => {
    void this.loadStagingReview();
  };

  private readonly handleDownloadTemplate = async (): Promise<void> => {
    if (!this.props.downloadTemplate) {
      return;
    }

    try {
      await this.props.downloadTemplate();
    } catch (error) {
      this.setState({
        errorMessage: this.getErrorMessage(error),
      });
    }
  };

  private readonly handleReopenForCorrection =
    async (): Promise<void> => {
      const loadSessionId = Number(this.state.loadSessionId);
      const stamp = String(this.state.stamp || '');

      if (!loadSessionId || !stamp) {
        this.setState({
          errorMessage:
            'A valid Load Session and concurrency stamp are required.',
        });
        return;
      }

      this.setState({
        reopening: true,
        errorMessage: '',
        successMessage: '',
      });

      try {
        const result = await reopenBieLoadSession({
          loadSessionId,
          stamp,
        });

        const setWizardParam =
          this.props.onParam(PAGE_LOAD_SESSION_WIZARD);

        setWizardParam('loadsessionid', loadSessionId);
        setWizardParam('stp', result.stamp);
        setWizardParam('mode', 'EDIT');

        this.setState({
          reopening: false,
          stateCode: 'DRAFT',
          stamp: result.stamp,
          activeStep: 1,
          selectedFile: null,
          successMessage:
            'Load Session reopened. Reset Staging before uploading the corrected workbook.',
        });
      } catch (error) {
        this.setState({
          reopening: false,
          errorMessage: this.getErrorMessage(error),
        });
      }
    };

  /* ==========================================================
   * Navigation
   * ==========================================================
   */

  private readonly handleBackToManager = (): void => {
    this.props.setPage(PAGE_LOAD_SESSION_MANAGER);
  };

  private readonly handleOpenWorkbench = (): void => {
    const loadSessionId = Number(this.state.loadSessionId || 0);

    if (loadSessionId <= 0) {
      this.setState({
        errorMessage:
          'A Load Session is required before opening the Workbench.',
      });

      return;
    }

    const setWorkbenchParam = this.props.onParam('set');

    setWorkbenchParam('loadsessionid', loadSessionId);
    setWorkbenchParam('stp', this.state.stamp);
    setWorkbenchParam('workbenchorigin', this.props.page || PAGE_LOAD_SESSION_WIZARD);

    this.props.setPage(PAGE_ONBOARDING_WORKBENCH);
  };

  /* ==========================================================
   * Form handlers
   * ==========================================================
   */

  private readonly handleShortcodeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    this.updateContext(
      'shortcode',
      event.target.value.trimStart().toUpperCase()
    );
  };

  private readonly handleNameChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    this.updateContext('name', event.target.value);
  };

  private readonly handleDescriptionChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    this.updateContext('description', event.target.value);
  };

  private readonly handleInstitutionNameChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const institutionName = event.target.value;

    this.setState((currentState) => ({
      context: {
        ...currentState.context,
        institutionName,
        sourceOrganization:
          currentState.context.sourceOrganization || institutionName,
      },
      contextErrors: {
        ...currentState.contextErrors,
        institutionName: undefined,
      },
    }));
  };

  private readonly handleInstitutionChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const value = event.target.value;

    const selected = (this.props.institutionOptions || []).find(
      (option) => String(option.rid) === String(value)
    );

    this.setState((currentState) => ({
      context: {
        ...currentState.context,
        institutionId: value === '' ? '' : Number(value),
        institutionCode: selected?.code || '',
        institutionName: selected?.name || '',
        sourceOrganization:
          currentState.context.sourceOrganization || selected?.name || '',
      },
      contextErrors: {
        ...currentState.contextErrors,
        institutionId: undefined,
        institutionName: undefined,
      },
    }));
  };

  private readonly handleSourceOrganizationChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    this.updateContext('sourceOrganization', event.target.value);
  };

  private readonly handleLoadScopeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const value = event.target.value;
    this.updateContext('scopeId', value === '' ? 0 : Number(value));
  };

  private readonly handleSourceSystemChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    this.updateContext('sourceSystem', event.target.value);
  };

  private readonly handleCoverageStartDateChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    this.updateContext('coverageStartDate', event.target.value);
  };

  private readonly handleCoverageEndDateChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    this.updateContext('coverageEndDate', event.target.value);
  };

  private readonly handleHistoricalCutoffDateChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    this.updateContext('historicalCutoffDate', event.target.value);
  };

  private updateContext<K extends keyof BieOnboardingLoadContext>(
    key: K,
    value: BieOnboardingLoadContext[K]
  ): void {
    this.setState((currentState) => ({
      context: {
        ...currentState.context,
        [key]: value,
      },
      contextErrors: {
        ...currentState.contextErrors,
        [key]: undefined,
      },
    }));
  }

  /* ==========================================================
   * Validation and payload mapping
   * ==========================================================
   */

  private validateContext(): Partial<
    Record<keyof BieOnboardingLoadContext, string>
  > {
    const context = this.state.context;

    const errors: Partial<
      Record<keyof BieOnboardingLoadContext, string>
    > = {};

    if (!String(context.shortcode || '').trim()) {
      errors.shortcode = 'Session Code is required.';
    }

    if (!String(context.name || '').trim()) {
      errors.name = 'Session Name is required.';
    }

    if ((this.props.institutionOptions || []).length > 0) {
      if (!context.institutionId) {
        errors.institutionId = 'Select the institution.';
      }
    } else if (!String(context.institutionName || '').trim()) {
      errors.institutionName = 'Institution is required.';
    }

    if (!String(context.sourceOrganization || '').trim()) {
      errors.sourceOrganization = 'Source Organization is required.';
    }

    if (!context.scopeId) {
      errors.scopeId = 'Load Scope is required.';
    }

    if (!String(context.sourceSystem || '').trim()) {
      errors.sourceSystem = 'Source System is required.';
    }

    if (!String(context.coverageStartDate || '').trim()) {
      errors.coverageStartDate = 'Coverage Start Date is required.';
    }

    if (!String(context.coverageEndDate || '').trim()) {
      errors.coverageEndDate = 'Coverage End Date is required.';
    }

    if (!String(context.historicalCutoffDate || '').trim()) {
      errors.historicalCutoffDate = 'Historical Cut-off Date is required.';
    }

    if (context.coverageStartDate && context.coverageEndDate &&
        context.coverageEndDate < context.coverageStartDate) {
      errors.coverageEndDate =
        'Coverage End Date cannot be earlier than Coverage Start Date.';
    }

    if (context.coverageEndDate && context.historicalCutoffDate &&
        context.historicalCutoffDate < context.coverageEndDate) {
      errors.historicalCutoffDate =
        'Historical Cut-off Date cannot be earlier than Coverage End Date.';
    }

    return errors;
  }

  private buildSavePayload(): BieLoadSessionAddPayload {
    const context = this.state.context;

    return {
      scopeId: Number(context.scopeId),
      name: String(context.name || '').trim(),
      shortcode: String(context.shortcode || '').trim().toUpperCase(),
      description: String(context.description || '').trim() || null,
      sourceOrganization: String(context.sourceOrganization || '').trim(),
      sourceSystem: String(context.sourceSystem || '').trim(),
      coverageStartDate: context.coverageStartDate,
      coverageEndDate: context.coverageEndDate,
      historicalCutoffDate: context.historicalCutoffDate,
      extraInfo: {
        ...(context.extraInfo || {}),
        institutionId: context.institutionId || null,
        institutionCode: context.institutionCode || '',
        institutionName: context.institutionName || '',
      },
    };
  }

  private extractContext(session: BieLoadSession): BieOnboardingLoadContext {
    return {
      scopeId: session.scopeId || 0,

      name: session.name,
      shortcode: session.shortcode,
      description: session.description || '',

      sourceOrganization: session.sourceOrganization || '',
      sourceSystem: session.sourceSystem || '',
      coverageStartDate: session.coverageStartDate || '',
      coverageEndDate: session.coverageEndDate || '',
      historicalCutoffDate: session.historicalCutoffDate || '',

      institutionId:
        Number(session.extraInfo?.institutionId || 0) || '',

      institutionCode: String(
        session.extraInfo?.institutionCode || ''
      ),

      institutionName: String(
        session.extraInfo?.institutionName ||
          session.sourceOrganization ||
          ''
      ),

      extraInfo: {
        ...(session.extraInfo || {}),
      },
    };
  }

  /* ==========================================================
   * State/readiness helpers
   * ==========================================================
   */

  private canEditContext(): boolean {
    if (this.state.mode === 'VIEW') {
      return false;
    }

    if (!this.state.loadSessionId) {
      return true;
    }

    return String(this.state.stateCode || '').toUpperCase() === 'DRAFT';
  }

  private canUploadWorkbook(): boolean {
    if (this.state.mode === 'VIEW' || !this.state.loadSessionId) {
      return false;
    }

    return ['DRAFT', 'STAGED', 'REJECTED'].includes(
      String(this.state.stateCode || '').toUpperCase()
    );
  }

  private isStepCompleted(stepIndex: number): boolean {
    switch (stepIndex) {
      case 0:
        return Boolean(this.state.loadSessionId);

      case 1:
        return Boolean(this.state.stagingReview?.workbookAvailable);

      case 2:
        return Boolean(
          this.state.stagingReview &&
            this.state.stagingReview.imports.length > 0
        );

      default:
        return false;
    }
  }

  private resolveInitialStep(
    requestedStep: BieOnboardingWizardStepCode,
    session: BieLoadSession,
    review: BieOnboardingWorkbookReview | null
  ): number {
    if (requestedStep === 'WORKBOOK_UPLOAD') {
      return 1;
    }

    if (requestedStep === 'STAGING_REVIEW' && review) {
      return 2;
    }

    if (review || session.workbook.available) {
      return 2;
    }

    return 0;
  }

  private normalizeStepCode(value: unknown): BieOnboardingWizardStepCode {
    const code = String(value || 'LOAD_CONTEXT').toUpperCase();

    if (code === 'WORKBOOK_UPLOAD' || code === 'STAGING_REVIEW') {
      return code;
    }

    return 'LOAD_CONTEXT';
  }

  private isValidYear(value: unknown): boolean {
    const year = Number(value);

    return Number.isInteger(year) && year >= 2000 && year <= 2200;
  }

  private isBusy(): boolean {
    return (
      this.state.loading ||
      this.state.saving ||
      this.state.uploading ||
      this.state.loadingReview
    );
  }

  /* ==========================================================
   * General helpers
   * ==========================================================
   */

  private readonly handleClearError = (): void => {
    this.setState({
      errorMessage: '',
    });
  };

  private readonly handleClearSuccess = (): void => {
    this.setState({
      successMessage: '',
    });
  };

  private getErrorMessage(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }

    return 'An unexpected onboarding error occurred.';
  }

  private formatFileSize(bytes: number): string {
    if (!Number.isFinite(bytes) || bytes <= 0) {
      return '0 KB';
    }

    if (bytes < 1024 * 1024) {
      return `${Math.max(1, Math.round(bytes / 1024))} KB`;
    }

    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }
}
